export const SYSTEM_TAGS = [
  'cold',
  'warm',
  'new',
  'vip',
  'phone-only',
  'stale',
  'high-value',
  'lost-price',
] as const;

export type TagGroup = 'temperature' | 'value' | 'risk' | 'info';
export const SYSTEM_TAG_GROUPS: Readonly<Record<string, TagGroup>> = {
  cold: 'temperature',
  warm: 'temperature',
  vip: 'value',
  'high-value': 'value',
  stale: 'risk',
  'lost-price': 'risk',
  new: 'info',
  'phone-only': 'info',
};

export function getTagGroup(value: string): TagGroup {
  return Object.hasOwn(SYSTEM_TAG_GROUPS, value) ? (SYSTEM_TAG_GROUPS[value] ?? 'info') : 'info';
}

export const SYSTEM_SOURCES = ['website', 'instagram', 'referral', 'phone', 'telegram'] as const;
const SOURCE_KEYS = Object.fromEntries(
  SYSTEM_SOURCES.map((value) => [value, `crm.enum.contact_source.${value}`]),
);
const ENUM_KEYS: Record<EnumKind, Readonly<Record<string, string>>> = {
  tag: Object.fromEntries(SYSTEM_TAGS.map((value) => [value, `crm.enum.tag.${value}`])),
  contactSource: SOURCE_KEYS,
  dealSource: SOURCE_KEYS,
  dealStage: Object.fromEntries(
    ['new', 'contacted', 'negotiation', 'won', 'lost'].map((value) => [
      value,
      `crm.enum.deal_stage.${value}`,
    ]),
  ),
};

export type EnumKind = 'tag' | 'contactSource' | 'dealSource' | 'dealStage';

/** System stages use semantic tones; custom stages stay neutral. */
export function getDealStageTone(value: string): EntityStatusTone {
  switch (value.toLowerCase()) {
    case 'contacted':
    case 'negotiation':
      return 'processing';
    case 'won':
      return 'success';
    case 'lost':
      return 'error';
    default:
      return 'default';
  }
}

/** Only exact system keys are localized; custom values remain untouched. */
export function getEnumLabel(kind: EnumKind, value: string, t: (key: string) => string): string {
  const keys = ENUM_KEYS[kind];
  const lookup = kind === 'dealStage' ? value.toLowerCase() : value;
  const key = Object.hasOwn(keys, lookup) ? keys[lookup] : undefined;
  return key ? t(key) : value;
}

export function matchesTagSearch(
  value: string,
  search: string,
  t: (key: string) => string,
): boolean {
  const needle = search.toLocaleLowerCase();
  return [value, getEnumLabel('tag', value, t)].some((text) =>
    text.toLocaleLowerCase().includes(needle),
  );
}
import type { EntityStatusTone } from '../../components/entity-detail/EntityStatusChip.js';
